/**
 * Flow graph edge model (DEC-188 decisions 2, 4, 5). Pure functions, no React.
 *
 * Every edge here exists in the backend (the DEC-188 edge table). Its state is
 * read ONLY from what the scan recorded: agent statuses, finding counts, the
 * per-agent `inputs` object and the scan's `deadFiles` count. Nothing is
 * inferred. When the datum an edge depends on was not recorded, the edge says
 * so ('not_recorded') rather than guessing.
 *
 * There is deliberately no D4 -> D12 edge: D12 reads nothing from D4 (DEC-188,
 * "Two defects in today's UI").
 */
import type { AgentState } from '../../../stores/scanStore'

/** What an agent actually received (DEC-188 decision 1). */
import type { AgentInputs } from '../../../types/agents'
export type { AgentInputs }

/** Kind of data an edge carries; drives its colour (DEC-188 decision 2). */
export type EdgeKind = 'plan' | 'history' | 'complex' | 'signal' | 'map' | 'findings' | 'order'

/**
 * passed:       data went across.
 * not_used:     the target ran and the data did not reach it (with a reason).
 * not_reached:  the target has not finished (or, on a saved scan, did not run).
 * not_recorded: the target finished, but what it received was not recorded.
 * order:        "runs next" (wave 3 order), no data ever passes.
 */
export type EdgeState = 'passed' | 'not_used' | 'not_reached' | 'not_recorded' | 'order'

export type GraphNodeId = string // agent ids plus the pseudo-nodes below

export const PSEUDO = {
  intake: 'intake',
  history: 'history',
  planner: 'planner',
  signal: 'signal',
  critic: 'critic',
  report: 'report',
} as const

export interface GraphEdge {
  id: string
  from: GraphNodeId
  to: GraphNodeId
  kind: EdgeKind
  state: EdgeState
  /** What the data is, one plain sentence. */
  info: string
  /** Why it is in this state. Empty for a plain pass. */
  reason: string
  /** Overrides the kind's short label on the canvas. */
  short?: string
}

export interface EdgeContext {
  agents: Record<string, AgentState>
  /** Count of D9 orphan modules that lowered cost; null when not recorded or not known yet. */
  deadFiles: number | null
  /** The scan has finished (Report written). */
  scanFinished: boolean
  /** The Critic has finished. */
  criticDone: boolean
  /** Rows rebuilt from the saved scan (DEC-185). */
  fromSaved: boolean
}

/** undefined = not received yet, null = not recorded (DEC-188 decision 1). */
export function inputsOf(a: AgentState | undefined): AgentInputs | null | undefined {
  if (!a) return undefined
  return a.inputs
}

const TERMINAL = ['complete', 'warned', 'failed', 'skipped', 'done_with_problems']

export function finished(a: AgentState | undefined): boolean {
  return !!a && TERMINAL.includes(a.status)
}

/** Ran to the end and produced output (not skipped, not failed). */
function ranOk(a: AgentState | undefined): boolean {
  return !!a && (a.status === 'complete' || a.status === 'warned' || a.status === 'done_with_problems')
}

function notReachedReason(a: AgentState | undefined, fromSaved: boolean): string {
  if (fromSaved && (!a || a.status === 'waiting')) return 'Did not run in this scan'
  if (a?.status === 'running') return 'Still running'
  return 'Not reached yet'
}

/** Shared gate for edges judged from the target's recorded inputs. */
function inputsGate(
  target: AgentState | undefined,
  ctx: EdgeContext,
): { state: EdgeState; reason: string } | AgentInputs {
  if (!finished(target)) return { state: 'not_reached', reason: notReachedReason(target, ctx.fromSaved) }
  if (target!.status === 'skipped') return { state: 'not_used', reason: `Skipped${target!.skip_reason ? `: ${target!.skip_reason}` : ''}` }
  const inputs = inputsOf(target)
  if (!inputs) return { state: 'not_recorded', reason: 'Not recorded for this scan' }
  return inputs
}

function isGate(g: { state: EdgeState; reason: string } | AgentInputs): g is { state: EdgeState; reason: string } {
  return 'state' in g
}

// ---------------------------------------------------------------------------
// One function per edge kind. Exported for the tests and for contextBadgeFor.
// ---------------------------------------------------------------------------

/** Planner -> agent (skip gate or file list): passed when the target ran. */
export function plannerEdgeState(target: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  if (!target || target.status === 'waiting') return { state: 'not_reached', reason: notReachedReason(target, ctx.fromSaved) }
  if (target.status === 'skipped') return { state: 'not_used', reason: `Skipped${target.skip_reason ? `: ${target.skip_reason}` : ''}` }
  return { state: 'passed', reason: '' }
}

/** Git history -> D4: passed iff inputs.repo_history === true. */
export function historyEdgeState(d4: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  const g = inputsGate(d4, ctx)
  if (isGate(g)) return g
  if (g.repo_history === true) return { state: 'passed', reason: '' }
  if (g.repo_history === false) return { state: 'not_used', reason: 'No git history' }
  return { state: 'not_used', reason: 'Git history did not apply to this scan' }
}

/** D1 -> D4 (complex files): passed iff inputs.complex_files > 0. */
export function complexEdgeState(d4: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  const g = inputsGate(d4, ctx)
  if (isGate(g)) return g
  if (typeof g.complex_files === 'number' && g.complex_files > 0) return { state: 'passed', reason: '' }
  if (g.complex_files === 0) return { state: 'not_used', reason: 'No complex files to pass on' }
  if (g.repo_history === false) return { state: 'not_used', reason: 'Needs git history, and there was none' }
  return { state: 'not_used', reason: 'Did not apply to this scan' }
}

/** Shared signal -> reader: passed iff inputs.llm_used && signal_files > 0. */
export function signalEdgeState(reader: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  const g = inputsGate(reader, ctx)
  if (isGate(g)) return g
  if (!g.llm_used) return { state: 'not_used', reason: 'AI step did not run' }
  if (g.signal_files > 0) return { state: 'passed', reason: '' }
  return { state: 'not_used', reason: 'No flagged files to place in its prompt' }
}

/** D12 -> D7 (service map): passed iff D7's inputs.coupling_graph === true. D12's finding count is irrelevant. */
export function serviceMapEdgeState(d7: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  const g = inputsGate(d7, ctx)
  if (isGate(g)) return g
  if (g.coupling_graph === true) return { state: 'passed', reason: '' }
  if (!g.llm_used) return { state: 'not_used', reason: 'AI step did not run' }
  if (g.coupling_graph === false) return { state: 'not_used', reason: 'Coupling found no services to map' }
  return { state: 'not_used', reason: 'Did not apply to this scan' }
}

/** Agent -> Critic: passed when the agent finished with findings. */
export function criticEdgeState(source: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  if (!finished(source)) return { state: 'not_reached', reason: notReachedReason(source, ctx.fromSaved) }
  if (source!.status === 'skipped') return { state: 'not_used', reason: 'Skipped' }
  // DEC-197: "Failed, no findings" only when it is true.
  if (source!.status === 'failed' && source!.findings_count === 0) return { state: 'not_used', reason: 'Failed, no findings' }
  if (source!.findings_count > 0) return { state: 'passed', reason: '' }
  return { state: 'not_used', reason: 'No findings' }
}

/** A wave's agents -> shared signal: passed once the wave finished with findings. */
export function signalWriteState(wave: AgentState[], ctx: EdgeContext): { state: EdgeState; reason: string } {
  const live = wave.filter((a) => a.status !== 'skipped')
  if (live.length === 0) return { state: 'not_used', reason: 'Every agent in this wave was skipped' }
  if (!live.every((a) => finished(a))) {
    return { state: 'not_reached', reason: ctx.fromSaved ? 'Did not run in this scan' : 'Wave still running' }
  }
  if (live.some((a) => ranOk(a) && a.findings_count > 0)) return { state: 'passed', reason: '' }
  return { state: 'not_used', reason: 'No files flagged in this wave' }
}

/** Critic -> Report: passed when the scan finished. */
export function reportEdgeState(ctx: EdgeContext): { state: EdgeState; reason: string } {
  return ctx.scanFinished ? { state: 'passed', reason: '' } : { state: 'not_reached', reason: 'Scan not finished yet' }
}

/** D9 -> Report (dead files): passed iff deadFiles > 0. */
export function deadFilesEdgeState(ctx: EdgeContext): { state: EdgeState; reason: string } {
  if (!ctx.scanFinished) return { state: 'not_reached', reason: 'Scan not finished yet' }
  if (ctx.deadFiles === null) return { state: 'not_recorded', reason: 'Not recorded for this scan' }
  if (ctx.deadFiles > 0) return { state: 'passed', reason: '' }
  return { state: 'not_used', reason: 'No dead files lowered the cost of other findings' }
}

/** Intake -> Planner: passed once the Planner has handed out work. */
export function intakeEdgeState(ctx: EdgeContext): { state: EdgeState; reason: string } {
  const started = ctx.fromSaved || Object.values(ctx.agents).some((a) => a.status !== 'waiting')
  return started ? { state: 'passed', reason: '' } : { state: 'not_reached', reason: 'Not reached yet' }
}

/** Order-only edge: "runs next", unlabelled. Faint until the target starts. */
export function orderEdgeState(target: AgentState | undefined, ctx: EdgeContext): { state: EdgeState; reason: string } {
  if (!target || target.status === 'waiting') return { state: 'not_reached', reason: notReachedReason(target, ctx.fromSaved) }
  return { state: 'order', reason: '' }
}

// ---------------------------------------------------------------------------
// The whole graph
// ---------------------------------------------------------------------------

export const SIGNAL_READERS = ['d3_testing', 'd5_security', 'd11_design', 'd4_architecture', 'd7_performance'] as const
export const ORDER_CHAIN = ['d9_dead_code', 'd4_architecture', 'd12_coupling', 'd7_performance'] as const

export function buildEdges(ctx: EdgeContext, waveAgents: Record<number, string[]>): GraphEdge[] {
  const A = ctx.agents
  const out: GraphEdge[] = []
  const push = (id: string, from: string, to: string, kind: EdgeKind, info: string, s: { state: EdgeState; reason: string }, short?: string) =>
    out.push({ id, from, to, kind, info, state: s.state, reason: s.reason, ...(short ? { short } : {}) })

  push('intake>planner', PSEUDO.intake, PSEUDO.planner, 'plan', 'File list and languages', intakeEdgeState(ctx))

  // Planner. D8 and D13 run on every file: an order line, no file list.
  for (const id of ['d8_lint', 'd13_infrastructure']) {
    if (A[id]) push(`planner>${id}`, PSEUDO.planner, id, 'order', 'Runs on all files, no file list', orderEdgeState(A[id], ctx))
  }
  push('planner>d2_dependency', PSEUDO.planner, 'd2_dependency', 'plan', 'Skip gate: runs only when a package manifest exists', plannerEdgeState(A.d2_dependency, ctx), 'skip gate')
  push('planner>d6_documentation', PSEUDO.planner, 'd6_documentation', 'plan', 'Which files to check', plannerEdgeState(A.d6_documentation, ctx))
  push('planner>d10_idiomatic', PSEUDO.planner, 'd10_idiomatic', 'plan', 'Which files to check (rubric languages, largest 500 on big repos)', plannerEdgeState(A.d10_idiomatic, ctx))

  push('history>d4_architecture', PSEUDO.history, 'd4_architecture', 'history', 'Who changed what and when (knowledge risk)', historyEdgeState(A.d4_architecture, ctx))
  push('d1_complexity>d4_architecture', 'd1_complexity', 'd4_architecture', 'complex', 'Complex files, for "abandoned complex code"', complexEdgeState(A.d4_architecture, ctx))

  // Shared signal: waves 1 and 2 write it; five agents read it in their AI prompt.
  const waveList = (n: number) => (waveAgents[n] ?? []).map((id) => A[id]).filter(Boolean)
  push('wave1>signal', 'd13_infrastructure', PSEUDO.signal, 'signal', 'Wave 1 flagged files', signalWriteState(waveList(1), ctx))
  push('wave2>signal', 'd11_design', PSEUDO.signal, 'signal', 'Wave 2 flagged files', signalWriteState(waveList(2), ctx))
  const readerInfo: Record<string, string> = {
    d3_testing: 'Wave 1 flagged files, in the AI prompt',
    d5_security: 'Wave 1 flagged files, in the AI prompt',
    d11_design: 'Wave 1 flagged files, in the AI prompt',
    d4_architecture: 'Flagged files from waves 1, 2 and Dead Code, in the AI prompt',
    d7_performance: 'Flagged files from every earlier agent, in the AI prompt',
  }
  for (const id of SIGNAL_READERS) push(`signal>${id}`, PSEUDO.signal, id, 'signal', readerInfo[id], signalEdgeState(A[id], ctx))

  // Wave 3 runs one after another. Only D12 -> D7 carries data.
  for (let i = 1; i < ORDER_CHAIN.length; i++) {
    const from = ORDER_CHAIN[i - 1]
    const to = ORDER_CHAIN[i]
    if (from === 'd12_coupling' && to === 'd7_performance') continue
    push(`${from}>${to}`, from, to, 'order', 'Runs next. No data passes here.', orderEdgeState(A[to], ctx))
  }
  push('d12_coupling>d7_performance', 'd12_coupling', 'd7_performance', 'map', 'Service map: services, links, chokepoints', serviceMapEdgeState(A.d7_performance, ctx))

  // Every agent feeds the Critic, which feeds the report.
  for (const n of [1, 2, 3, 4]) {
    for (const id of waveAgents[n] ?? []) {
      if (A[id]) push(`${id}>critic`, id, PSEUDO.critic, 'findings', 'Findings', criticEdgeState(A[id], ctx))
    }
  }
  push('critic>report', PSEUDO.critic, PSEUDO.report, 'findings', 'Reviewed findings, then cost and Debt Index', reportEdgeState(ctx))
  push('d9_dead_code>report', 'd9_dead_code', PSEUDO.report, 'findings', 'Dead files lower the cost of other findings in them', deadFilesEdgeState(ctx))

  return out
}

/** True when at least one edge could not be judged because its inputs were not recorded. */
export function anyNotRecorded(edges: GraphEdge[]): boolean {
  return edges.some((e) => e.state === 'not_recorded')
}

export const STATE_TEXT: Record<EdgeState, string> = {
  passed: 'Data passed',
  not_used: 'Not used this scan',
  not_reached: 'Not reached yet',
  not_recorded: 'Not recorded',
  order: 'Order only, no data',
}

/** Short label drawn on a selected agent's lines. */
export const KIND_SHORT: Record<EdgeKind, string> = {
  plan: 'file list',
  history: 'git history',
  complex: 'complex files',
  signal: 'flagged files',
  map: 'service map',
  findings: 'findings',
  order: 'runs next',
}

export function edgeLabel(e: GraphEdge): string {
  const short = e.short ?? KIND_SHORT[e.kind]
  // DEC-188: a skipped D2 carries its reason on the line itself.
  if (e.id === 'planner>d2_dependency' && e.state === 'not_used') return `${short} · ${e.reason.toLowerCase()}`
  const suffix =
    e.state === 'not_used' ? ' · not used' : e.state === 'not_reached' ? ' · not yet' : e.state === 'not_recorded' ? ' · not recorded' : ''
  return short + suffix
}
